/**
 * Shared display-form serializer for a typed section's `value`. Used by both export paths:
 * markdown (client-side, this copy) and PDF (server-side, `apps/workspace-api/src/briefs/export/format-section-value.ts`
 * — a second, pinned-equal copy; PDF export runs in workspace-api, not the browser, so the two
 * cannot share one module).
 *
 * Never throws — an unrecognized type or a malformed value renders as an empty string rather
 * than breaking an export.
 */

export interface FormatSectionValueOptions {
  /** Resolves an assetId to a display name for the `file` type. Falls back to the raw id. */
  resolveAssetName?: (assetId: string) => string;
}

export function formatSectionValue(
  type: string | null,
  config: Record<string, unknown>,
  value: unknown,
  options: FormatSectionValueOptions = {},
): string {
  if (type == null || type === 'text') return '';
  if (value == null || typeof value !== 'object') return '';
  const v = value as Record<string, unknown>;

  switch (type) {
    case 'date': {
      const start = typeof v.start === 'string' ? v.start : null;
      const end = typeof v.end === 'string' ? v.end : null;
      if (!start && !end) return '';
      if (start && end) return `${start} – ${end}`;
      return start ?? end ?? '';
    }
    case 'select':
    case 'checklist': {
      const selected = Array.isArray(v.selected) ? (v.selected as unknown[]) : [];
      return selected.filter((s): s is string => typeof s === 'string').join(', ');
    }
    case 'url':
      return typeof v.url === 'string' ? v.url : '';
    case 'file': {
      const assetIds = Array.isArray(v.assetIds) ? (v.assetIds as unknown[]) : [];
      return assetIds
        .filter((id): id is string => typeof id === 'string')
        .map((id) => options.resolveAssetName?.(id) ?? id)
        .join(', ');
    }
    case 'boolean': {
      if (v.value == null) return '';
      // Seed defaults, not copy — same reasoning as `DEFAULT_SECTION_CONFIG.boolean` in
      // `sectionTypes.ts`: pinned byte-for-byte to the backend's English default.
      // eslint-disable-next-line i18next/no-literal-string
      const trueLabel = typeof config.trueLabel === 'string' ? config.trueLabel : 'Yes';
      // eslint-disable-next-line i18next/no-literal-string
      const falseLabel = typeof config.falseLabel === 'string' ? config.falseLabel : 'No';
      return v.value === true ? trueLabel : falseLabel;
    }
    default:
      return '';
  }
}
